import * as React from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { useLocation } from 'wouter';
import {
  CmdCheckboxGroup,
  CmdInput,
  CmdLabel,
  CmdSimpleSelect,
  CmdTextarea,
  CmdSubpage,
  CmdModalFooterButton,
  CmdSuperSelect,
} from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { useQueries } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../../Services/FormikValidatorService';
import PermissionsService, {
  PERMISSION_REFUELING_CARDS,
  PERMISSION_VEHICLE,
} from '../../../Services/PermissionsService';
import { IOption } from '../../Forms/CmdField';
import usePageWithForm from '../../../hooks/usePageWithForm';
import FuelCard from './FuelCard';
import useUrlQueryParams from '../../../hooks/useUrlQueryParams';
import { ITripsOverviewChipOption } from '../../TripsOverviewNew/interfaces';
import SimpleSelectWithSearchHookForm from '../../Forms/Fields/SimpleSelectWithSearchHookForm';
import Calendar from '../../Forms/ReactHookFormFields/Calendar';
import {
  editVehicle,
  getCostCenterIdOptions,
  getDefaultDriverIdOptions,
  getEditingData,
  getFuelCardsOptions,
  getFuelCardsValues,
  getFuelTypeListOptions,
  getValidation,
  getVehicleTypeIdOptions,
} from '../VehicleSettingsService';
import { handleChipOptionsCategory } from '../../TripsOverviewNew/TripsOverviewService';
import { transformErrors } from '../../../Services/HookFormService';
import useAnalytics from '../../../hooks/useAnalytics';

interface ModalFormProps {
  handleClose: () => void;
  id: number | null;
  customerId: number | null;
  updateTable: () => void;
}

interface IEquipment {
  children: React.ReactNode[];
  id: number;
  name: string;
  title: string;
  type: string;
  value: boolean;
}

interface IVehicleSettingInputForm {
  autoCorrection: boolean;
  axleCount: number;
  axleDriveCount: number;
  can: string;
  color: string;
  commissioning: string;
  cons100kmNorm: number | null;
  cons100kmTheoretical: number;
  costCenterId: string;
  customer: string;
  defaultDriverId: number;
  dryWeight: string;
  edEnabled: string;
  emissionInspectionValidTo: string;
  engineCode: string;
  engineCubature: string;
  equipment: string[];
  fuelCardId: string[];
  fuelTypeId: number;
  initialMileage: string;
  licenceNumber: string;
  manufactureYear: string;
  manufacturer: '';
  model: string;
  note: string;
  privateVehicle: string;
  privateVehicleDefault: string;
  simCardPhoneNumber: string;
  technicalInspectionValidTo: string;
  tireCount: number;
  totalCapacity: number;
  unit: string;
  vehicleName: string;
  vehicleTypeId: number;
  vin: string;
  first_aid_kit?: Date | null;
  first_aid_kit_dis?: string;
  cardReaderType: string | null;
  vehicleBlockingAvailable: boolean;
  vehicleBlockingDuringRideAvailable: boolean;
  bluecoinEnabled: boolean;
  autoCorrectionFrequency: string;
  autoCorrectionMinDistance: number;
}

export default function Form(props: ModalFormProps): JSX.Element {
  usePageWithForm();
  const { formatMessage: f } = useIntl();
  const { id } = props;
  const [pathname] = useLocation();
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const [isMedicalKit, setIsMedicalKit] = React.useState<boolean>(false);
  const [equipmentData, setEquipmentData] = React.useState<IEquipment[]>([]);
  const [medicalKitBoolean, setMedicalKitBoolean] = React.useState<boolean>(false);
  const [fuelCardsOptions, setFuelCardsOptions] = React.useState<ITripsOverviewChipOption[]>([]);
  const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
  const query = useUrlQueryParams();
  const customerIdUrl = query.get('customerId');
  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);
  const [equipmentOptions, setEquipmentOptions] = React.useState<IOption[]>([]);
  const [vehicleTypeOptions, setVehicleTypeOptions] = React.useState<IOption[]>([]);
  const [defaultDriverOptions, setDefaultDriverOptions] = React.useState<IOption[]>([]);
  const [costCenterOptions, setCostCenterOptions] = React.useState<IOption[]>([]);
  const [fuelTypeOptions, setFuelTypeOptions] = React.useState<IOption[]>([]);
  const { trackEvent } = useAnalytics();

  const validateFields = (values: IVehicleSettingInputForm) => {
    let formErrors: any = {};
    if (!validation) {
      return formErrors;
    }
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, validation);
    if (!values.manufactureYear) {
      delete formErrors.manufactureYear;
    }
    if (formErrors.cons100kmNorm) {
      delete formErrors.cons100kmNorm;
    }
    return transformErrors(formErrors);
  };

  const {
    handleSubmit,
    watch,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<IVehicleSettingInputForm>({
    mode: 'onChange',
    defaultValues: {
      autoCorrection: false,
      axleCount: 0,
      axleDriveCount: 0,
      can: '',
      color: '',
      commissioning: '',
      cons100kmNorm: null,
      cons100kmTheoretical: 0,
      costCenterId: '',
      customer: '',
      defaultDriverId: 0,
      dryWeight: '',
      edEnabled: '',
      emissionInspectionValidTo: '',
      engineCode: '',
      engineCubature: '',
      equipment: [],
      fuelCardId: [],
      fuelTypeId: 0,
      initialMileage: '',
      licenceNumber: '',
      manufactureYear: '',
      manufacturer: '',
      model: '',
      note: '',
      privateVehicle: '',
      privateVehicleDefault: '',
      simCardPhoneNumber: '',
      technicalInspectionValidTo: '',
      tireCount: 0,
      totalCapacity: 0,
      unit: '',
      vehicleName: '',
      vehicleTypeId: 0,
      vin: '',
      first_aid_kit: null,
      first_aid_kit_dis: '',
      cardReaderType: null,
      vehicleBlockingAvailable: false,
      vehicleBlockingDuringRideAvailable: false,
      bluecoinEnabled: false,
      autoCorrectionFrequency: '',
      autoCorrectionMinDistance: 0,
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const isAllowed = PermissionsService.hasAccess(PERMISSION_VEHICLE, ['update'], props.customerId);

  const getEquipmentOptions = (items: any) => {
    return items.map((i: any) => {
      return {
        value: String(i.id),
        item: f({ id: i.title }),
      };
    });
  };

  const getEquipmentValue = (items: any) => {
    const values: string[] = [];
    items.forEach((i: any) => {
      if (i.value === true) {
        values.push(String(i.id));
      }
    });
    return values;
  };

  const getMedicalKitValue = (items: IEquipment[]) => {
    let value: Date = new Date();
    items.forEach((i: any) => {
      if (i.name === 'first_aid_kit' && i.children && i.children[0]) {
        value = new Date(i.children[0].value);
      }
    });
    return value;
  };

  const getMedicalKitBoolean = (equipValues: string[], origEquipmentData?: any) => {
    const medicKit = (origEquipmentData || equipmentData).find(
      (i: any) => i.name === 'first_aid_kit'
    );
    setIsMedicalKit(!!medicKit);
    if (medicKit && equipValues.includes(String(medicKit.id))) {
      return true;
    }
    return false;
  };

  const handleMedicalKitBoolean = (value: string[], origEquipmentData?: any) => {
    const boolean = getMedicalKitBoolean(value, origEquipmentData);
    setMedicalKitBoolean(boolean);
  };

  const getData = async () => {
    setIsLoaderActive(true);
    const tempPath = pathname.split('/');
    const inputs = await getEditingData(id || tempPath[tempPath.length - 1]);
    setIsLoaderActive(false);
    if (inputs) {
      Object.keys(inputs).forEach((index: string) => {
        if (index === 'tanks') {
          if (inputs.tanks && inputs.tanks[0]) {
            setValue(
              'cons100kmNorm',
              inputs.tanks[0].cons100kmNorm
              // inputs.tanks[0].cons100kmNorm === null ? 0 : inputs.tanks[0].cons100kmNorm
            );
            setValue('cons100kmTheoretical', inputs.tanks[0].cons100kmTheoretical);
            setValue('fuelTypeId', inputs.tanks[0].fuelTypeId);
            setValue('totalCapacity', inputs.tanks[0].totalCapacity);
          }
        } else if (index === 'equipment') {
          setEquipmentData(inputs.equipment);
          setEquipmentOptions(getEquipmentOptions(inputs.equipment));
          const eqValue = getEquipmentValue(inputs.equipment);
          setValue('equipment', eqValue);
          handleMedicalKitBoolean(eqValue, inputs.equipment);
          setValue('first_aid_kit', getMedicalKitValue(inputs.equipment));
        } else if (index === 'vehicleTypeId' && inputs.vehicleTypeId?.id) {
          setValue('vehicleTypeId', inputs.vehicleTypeId.id);
        } else {
          setValue(index, inputs[index]);
        }
      });
    }
  };

  const getVehicleSettingsValidation = async () => {
    const response = await getValidation();
    if (response) {
      setValidation(response);
    }
  };

  const getVehicleTypeIdOptionsData = async () => {
    const response = await getVehicleTypeIdOptions();
    if (response) {
      setVehicleTypeOptions(response);
    }
  };

  const getDefaultDriverIdOptionsData = async () => {
    const response = await getDefaultDriverIdOptions(props.customerId);
    if (response) {
      setDefaultDriverOptions(response);
    }
  };

  const getCostCenterIdOptionsData = async () => {
    const tempPath = pathname.split('/');
    const response = await getCostCenterIdOptions(props.id || tempPath[tempPath.length - 1]);
    if (response) {
      setCostCenterOptions(response);
    }
  };

  const getFuelTypeListOptionsData = async () => {
    const response = await getFuelTypeListOptions();
    if (response) {
      setFuelTypeOptions(response);
    }
  };

  const getEquipmentDataToEdit = (values: string[], date?: Date | null) => {
    return equipmentData.map((i: any) => {
      if (values.includes(String(i.id))) {
        i.value = true;
      } else {
        i.value = false;
      }
      if (i.name === 'first_aid_kit' && i.children && date) {
        i.children[0].value = date;
      }
      return i;
    });
  };

  const onSubmit = async (values: IVehicleSettingInputForm) => {
    const newValues = { ...values, cons100kmNorm: values.cons100kmNorm || null };
    newValues.equipment = getEquipmentDataToEdit(values.equipment, values.first_aid_kit);
    delete newValues.first_aid_kit;
    delete newValues.first_aid_kit_dis;
    // newValues.fuelCardId = filtedCardsId;
    setIsLoaderActive(true);

    const response = await editVehicle(newValues, props.id);
    setIsLoaderActive(false);
    if (response) {
      props.handleClose();
      props.updateTable();
    }
    trackEvent(['vehicle form', 'click', 'Submit form']);
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'rides.modal.close',
      type: 'button',
      title: f({ id: 'form.close' }),
      className: 'e-button',
      closeCallback: props.handleClose,
    },
  ];

  if (isAllowed) {
    buttons.push({
      id: 'subpage-button-submit',
      type: 'submit',
      title: f({ id: 'form.save' }),
      className: 'e-button e-button--gray',
      submitCallback: handleSubmit(onSubmit),
    });
  }

  const getLabelById = (value: string | number, options: IOption[]): string => {
    if (options) {
      const label = options.find((option: IOption) => String(option.value) === String(value));
      if (label) {
        return label.item;
      }
    }
    return '';
  };

  const getValueByOptions = (options: IOption[], value: string[]) => {
    let text = '';
    options.forEach((i: any) => {
      if (value.includes(String(i.value))) {
        text += `${i.item} `;
      }
    });
    return text;
  };

  const plusButtonCallback = () => {
    setIsModalOpened(true);
    trackEvent(['vehicle form', 'click', 'Add fuel card']);
  };

  const closeModal = () => {
    setIsModalOpened(false);
    trackEvent(['vehicle form', 'click', 'Close fuel card modal']);
  };

  const handleNewFuelCard = (newFuelCard) => {
    setFuelCardsOptions((oldValue) => {
      const newValue = [...oldValue];
      newValue.push({
        item: `${newFuelCard.providerName ? `${newFuelCard.providerName}-` : ''}${
          newFuelCard.identifier
        }-${newFuelCard.cardNumber}`,
        value: newFuelCard.id,
        shortChipName: `${newFuelCard.providerName ? `${newFuelCard.providerName}-` : ''}${
          newFuelCard.identifier
        }`,
        popup: `${newFuelCard.providerName ? `${newFuelCard.providerName}-` : ''}${
          newFuelCard.identifier
        }-${newFuelCard.cardNumber}`,
      });
      return newValue;
    });

    const value = watch('fuelCardId') || [];
    value.push(String(newFuelCard.id));
    setValue('fuelCardId', value);
  };

  const getFuelCardsValuesData = async () => {
    const response = await getFuelCardsValues(props.id);
    if (response) {
      let fuelCardsValues = response;

      if (!isAllowed) {
        fuelCardsValues = fuelCardsOptions
          .filter((item) => response.includes(item.value))
          .map((item) => `${item.item}`)
          .join(', ');
      }

      setValue('fuelCardId', fuelCardsValues);
      trigger('fuelCardId');
    }
  };

  const getFuelCardsOptionsData = async () => {
    const response = await getFuelCardsOptions(customerIdUrl, props.id);
    if (response) {
      const filteredOptions = response.map((item) => {
        return {
          ...item,
          value: String(item.value),
          label: '',
          item: `${item.providerName ? `${item.providerName}-` : ''}${item.item}-${
            item.cardNumber
          }`,
          shortChipName: `${item.providerName ? `${item.providerName}-` : ''}${item.item}`,
          popup: `${item.providerName ? `${item.providerName}-` : ''}${item.item}-${
            item.cardNumber
          }`,
        };
      });
      setFuelCardsOptions(filteredOptions);
      if (filteredOptions.length > 0) {
        getFuelCardsValuesData();
      }
    }

    return false;
  };

  useQueries({
    queries: [
      {
        queryKey: ['vehicleFuelCardOptions'],
        queryFn: () => getFuelCardsOptionsData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['carSettingsValidation'],
        queryFn: () => getVehicleSettingsValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: isAllowed,
      },
      {
        queryKey: ['carsSettingsData'],
        queryFn: () => getData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!(pathname && id),
      },
      {
        queryKey: ['carSettingsVehicleTypeIdOptions'],
        queryFn: () => getVehicleTypeIdOptionsData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['carSettingsFuelTypeListOptions'],
        queryFn: () => getFuelTypeListOptionsData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['carSettingsDefaultDriverIdOptions'],
        queryFn: () => getDefaultDriverIdOptionsData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['carSettingsCostCenterIdOptions'],
        queryFn: () => getCostCenterIdOptionsData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const handleComissioningRange = () => {
    let minDateRule;
    if (validation && validation.commissioning) {
      minDateRule = validation.commissioning.find((item) => item.param === 'minDate');
    }
    return [
      new Date(dayjs(minDateRule ? new Date(minDateRule.value) : new Date()).format('YYYY-MM-DD')),
      new Date(dayjs(new Date()).format('YYYY-MM-DD')),
    ];
  };

  const getAutoCorrectionValue = () => {
    let value = watch('autoCorrection')
      ? f({ id: 'enum.status.enabledF' })
      : f({ id: 'enum.status.disabledF' });

    if (watch('autoCorrection')) {
      value += ` (${watch('autoCorrectionFrequency')} ${f({ id: 'vehicleSettings.after' })} ${watch('autoCorrectionMinDistance')} km)`;
    }
    return value;
  };

  const getVehicleBlockingAvailableText = () => {
    let value = watch('vehicleBlockingAvailable')
      ? f({ id: 'vehicleSettings.availableF' })
      : f({ id: 'vehicleSettings.unavailableF' });
    if (watch('vehicleBlockingAvailable')) {
      value += watch('vehicleBlockingDuringRideAvailable')
        ? ` (${f({ id: 'vehicleSettings.allowed' })})`
        : ` (${f({ id: 'vehicleSettings.prohibited' })})`;
    }
    return value;
  };

  const getCardReaderType = () => {
    if (watch('cardReaderType') === 'rfid') {
      return 'RFID';
    }
    if (watch('cardReaderType') === 'dallas') {
      return 'DALLAS';
    }
    return f({ id: 'vehicle.noneF' });
  };

  return (
    <CmdSubpage
      id="vehicle-edit-form"
      title={f({ id: `tab.vehicle` })}
      subtitle={watch('licenceNumber')}
      buttons={buttons}
      loader={isLoaderActive}
      maintitle={f({ id: `menu.settings` })}
    >
      {isAllowed ? (
        <div>
          <div className="row">
            <div className="col-xxl-6">
              <CmdBox title={f({ id: 'vehicle.baseData' })} id="basic-information">
                <CmdLabel
                  id="basic-information-customer"
                  title={f({ id: 'systemSettings.customer' })}
                  value={watch('customer')}
                />
                <CmdLabel
                  id="basic-information-license-plate"
                  title={f({ id: 'vehicle.licenceNumber' })}
                  value={watch('licenceNumber')}
                />
                <Controller
                  name="manufacturer"
                  control={control}
                  rules={{
                    required: errors.manufacturer ? errors.manufacturer.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'form.inputs.vehicles.manufacturer' })}
                      id="manufacturer"
                      name="manufacturer"
                      type="text"
                      onChangeCallback={(value) => {
                        setValue('manufacturer', value);
                        trigger('manufacturer');
                      }}
                      error={errors.manufacturer?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="vehicleName"
                  control={control}
                  rules={{
                    required: errors.vehicleName ? errors.vehicleName.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'vehicle.vehicleName' })}
                      id="vehicleName"
                      name="vehicleName"
                      type="text"
                      placeholder={f({ id: 'form.choose' })}
                      required={true}
                      onChangeCallback={(value) => {
                        setValue('vehicleName', value);
                        trigger('vehicleName');
                      }}
                      error={errors.vehicleName?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="vehicleTypeId"
                  control={control}
                  rules={{
                    required: errors.vehicleTypeId ? errors.vehicleTypeId.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdSimpleSelect
                      {...field}
                      label={f({ id: 'vehicle.vehicleTypeId' })}
                      id="vehicleTypeId"
                      name="vehicleTypeId"
                      introOption={f({ id: 'form.choose' })}
                      introDisabled={false}
                      options={vehicleTypeOptions}
                      value={String(watch('vehicleTypeId'))}
                      required={true}
                      tooltip={f({ id: 'vehicle.vehicleTypeId.tooltip' })}
                      onChangeCallback={(value) => {
                        setValue('vehicleTypeId', value);
                        trigger('vehicleTypeId');
                        trackEvent(['vehicle form', 'change', 'Vehicle type']);
                      }}
                      error={errors.vehicleTypeId?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="defaultDriverId"
                  control={control}
                  render={({ field }) => (
                    <SimpleSelectWithSearchHookForm
                      {...field}
                      label={f({ id: 'vehicle.defaultDriverId' })}
                      id="defaultDriverId"
                      name="defaultDriverId"
                      introOption={f({ id: 'form.choose' })}
                      options={defaultDriverOptions}
                      searchPlaceholder={f({ id: 'form.search' })}
                      control={control}
                      canRemoveValue={true}
                      tooltip={f({ id: 'vehicle.defaultDriverId.tooltip' })}
                      onChangeCallback={(value) => {
                        setValue('defaultDriverId', value);
                        trigger('defaultDriverId');
                        trackEvent(['vehicle form', 'change', 'Default driver']);
                      }}
                    />
                  )}
                />
                <Controller
                  name="fuelCardId"
                  control={control}
                  rules={{
                    required: errors.fuelCardId ? errors.fuelCardId.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdSuperSelect
                      {...field}
                      label={f({ id: 'vehicle.fuelCardId' })}
                      id="fuelCardId"
                      name="fuelCardId"
                      plusButtonCallback={plusButtonCallback}
                      plusButtonTooltip={f({ id: 'vehicleSettings.form.fuelCard.add.tooltip' })}
                      options={handleChipOptionsCategory(
                        fuelCardsOptions,
                        watch('fuelCardId') || [],
                        [
                          f({ id: 'alarms.modal.rule.choice.select' }),
                          f({ id: 'alarms.modal.rule.choice.not.select' }),
                        ]
                      )}
                      searchPlaceholder={f({ id: 'form.search' })}
                      searchTooltip={f({
                        id: 'alarms.section.vehicles.driverId.search.tooltip',
                      })}
                      tooltip={f({ id: 'vehicle.fuelCardId.tooltip' })}
                      hasButton
                      isArrayValue={true}
                      onChangeCallback={(value) => {
                        setValue('fuelCardId', value);
                        trigger('fuelCardId');
                        trackEvent(['vehicle form', 'change', 'Fuel card selection']);
                      }}
                      disableTooltipOnChips
                      error={errors.fuelCardId?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="costCenterId"
                  control={control}
                  rules={{
                    required: errors.costCenterId ? errors.costCenterId.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdSimpleSelect
                      {...field}
                      label={f({ id: 'vehicle.costCenterId' })}
                      id="costCenterId"
                      name="costCenterId"
                      introOption={f({ id: 'form.choose' })}
                      introDisabled={false}
                      options={costCenterOptions}
                      onChangeCallback={(value: string) => {
                        setValue('costCenterId', value);
                        trigger('costCenterId');
                        trackEvent(['vehicle form', 'change', 'Select cost center']);
                      }}
                      error={errors.costCenterId?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="note"
                  control={control}
                  rules={{
                    required: errors.note ? errors.note.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdTextarea
                      {...field}
                      label={f({ id: 'vehicle.note' })}
                      id="note"
                      name="note"
                      rows={5}
                      setValue={(value: string) => {
                        setValue('note', value);
                        trigger('note');
                      }}
                      error={errors.note?.message}
                      touched={true}
                    />
                  )}
                />
              </CmdBox>
              <CmdBox title={f({ id: 'vehicle.vehicleTank' })} id="tank">
                <Controller
                  name="fuelTypeId"
                  control={control}
                  rules={{
                    required: errors.fuelTypeId ? errors.fuelTypeId.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdSimpleSelect
                      {...field}
                      label={f({ id: 'vehicle.fuelTypeId' })}
                      id="fuelTypeId"
                      name="fuelTypeId"
                      introOption={f({ id: 'form.choose' })}
                      introDisabled={false}
                      options={fuelTypeOptions}
                      required={true}
                      value={String(watch('fuelTypeId'))}
                      tooltip={f({ id: 'vehicle.fuelTypeId.tooltip' })}
                      onChangeCallback={(value) => {
                        setValue('fuelTypeId', value);
                        trigger('fuelTypeId');
                        trackEvent(['vehicle form', 'change', 'Select fuel type']);
                      }}
                      error={errors.fuelTypeId?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="totalCapacity"
                  control={control}
                  rules={{
                    required: errors.totalCapacity ? errors.totalCapacity.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'vehicle.totalCapacity' })}
                      id="totalCapacity"
                      name="totalCapacity"
                      type="number"
                      required={true}
                      tooltip={f({ id: 'vehicle.totalCapacity.tooltip' })}
                      onChangeCallback={(value) => {
                        setValue('totalCapacity', value);
                        trigger('totalCapacity');
                      }}
                      error={errors.totalCapacity?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="cons100kmTheoretical"
                  control={control}
                  rules={{
                    required: errors.cons100kmTheoretical
                      ? errors.cons100kmTheoretical.message
                      : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'vehicle.cons100kmTheoretical' })}
                      id="cons100kmTheoretical"
                      name="cons100kmTheoretical"
                      type="number"
                      required={true}
                      tooltip={f({ id: 'vehicle.cons100kmTheoretical.tooltip' })}
                      onChangeCallback={(value) => {
                        setValue('cons100kmTheoretical', value);
                        trigger('cons100kmTheoretical');
                      }}
                      error={errors.cons100kmTheoretical?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="cons100kmNorm"
                  control={control}
                  rules={{
                    required: errors.cons100kmNorm ? errors.cons100kmNorm.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'vehicle.cons100kmCombined' })}
                      id="cons100kmNorm"
                      name="cons100kmNorm"
                      type="number"
                      tooltip={f({ id: 'vehicle.cons100kmCombined.tooltip' })}
                      onChangeCallback={(value) => {
                        setValue('cons100kmNorm', value);
                        trigger('cons100kmNorm');
                      }}
                      error={errors.cons100kmNorm?.message}
                      touched={true}
                    />
                  )}
                />
              </CmdBox>

              <CmdBox title={f({ id: 'vehicle.vehicleMonitoring' })} id="monitoring">
                <CmdLabel
                  id="monitoring-odometer"
                  title={f({ id: 'vehicle.initialMileage' })}
                  value={`${watch('initialMileage')} km`}
                  tooltip={f({ id: 'vehicle.initialMileage.tooltip' })}
                />
                <CmdLabel
                  id="monitoring-unit"
                  title={f({ id: 'vehicle.unit' })}
                  value={watch('unit') || f({ id: 'vehicle.noneF' })}
                  tooltip={f({ id: 'vehicle.unit.tooltip' })}
                />
                <CmdLabel
                  id="monitoring-phone-number"
                  title={f({ id: 'table_list.columns.vehicles.phoneNumber' })}
                  value={watch('simCardPhoneNumber')}
                  tooltip={f({ id: 'vehicle.phoneNumber.tooltip' })}
                />
                <CmdLabel
                  id="monitoring-can"
                  title={f({ id: 'vehicle.can' })}
                  value={watch('can') || f({ id: 'vehicle.noneM' })}
                  tooltip={f({ id: 'vehicle.can.tooltip' })}
                />
                <CmdLabel
                  id="cardReaderType"
                  title={f({ id: 'vehicleSettings.cardReaderType' })}
                  value={getCardReaderType()}
                  tooltip={f({ id: 'vehicleSettings.cardReaderType.tooltip' })}
                />
                <CmdLabel
                  id="vehicleBlockingAvailable"
                  title={f({ id: 'vehicleSettings.vehicleBlockingAvailable' })}
                  value={getVehicleBlockingAvailableText()}
                  tooltip={f({ id: 'vehicleSettings.vehicleBlockingAvailable.tooltip' })}
                />
                <CmdLabel
                  id="monitoring-private-default"
                  title={f({ id: 'vehicle.privateVehicleDefault' })}
                  value={watch('privateVehicleDefault')}
                />
                <CmdLabel
                  id="monitoring-private"
                  title={f({ id: 'vehicle.privateVehicle' })}
                  value={watch('privateVehicle')}
                  tooltip={f({ id: 'vehicle.privateVehicle.tooltip' })}
                />
                <CmdLabel
                  id="autoCorrection"
                  title={f({ id: 'form.inputs.vehicles.autoCorrection' })}
                  value={getAutoCorrectionValue()}
                  tooltip={f({ id: 'vehicle.autoCorrection.tooltip' })}
                />
                <CmdLabel
                  id="edEnabled"
                  title={f({ id: 'form.inputs.vehicles.edEnabled' })}
                  value={
                    watch('edEnabled')
                      ? f({ id: 'enum.status.enabledM' })
                      : f({ id: 'enum.status.disabledM' })
                  }
                  tooltip={f({ id: 'vehicle.edEnabled.tooltip' })}
                />
                <CmdLabel
                  id="bluecoinEnabled"
                  title={f({ id: 'vehicleSettings.bluecoinEnabled' })}
                  value={
                    watch('bluecoinEnabled')
                      ? f({ id: 'vehicleSettings.enabledM' })
                      : f({ id: 'vehicleSettings.disabledM' })
                  }
                  tooltip={f({ id: 'vehicleSettings.bluecoinEnabled.tooltip' })}
                />
              </CmdBox>
            </div>
            <div className="col-xxl-6">
              <CmdBox title={f({ id: 'vehicle.technicalParams' })} id="params">
                <Controller
                  name="model"
                  control={control}
                  rules={{
                    required: errors.model ? errors.model.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'vehicle.model' })}
                      id="model"
                      name="model"
                      type="text"
                      onChangeCallback={(value) => {
                        setValue('model', value);
                        trigger('model');
                      }}
                      error={errors.model?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="manufactureYear"
                  control={control}
                  rules={{
                    required: errors.manufactureYear ? errors.manufactureYear.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'vehicle.manufactureYear' })}
                      id="manufactureYear"
                      name="manufactureYear"
                      type="number"
                      onChangeCallback={(value) => {
                        setValue('manufactureYear', value);
                        trigger('manufactureYear');
                      }}
                      error={errors.manufactureYear?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="commissioning"
                  control={control}
                  rules={{
                    required: errors.commissioning ? errors.commissioning.message : undefined,
                  }}
                  render={({ field }) => (
                    <Calendar
                      {...field}
                      label={f({ id: 'vehicle.commissioning' })}
                      id="commissioning"
                      dateFormat="dd.MM.yyyy"
                      noTz={true}
                      allowedRange={handleComissioningRange()}
                      onChangeCallback={(value) => {
                        setValue('commissioning', value);
                        trigger('commissioning');
                        trackEvent(['vehicle form', 'change', 'Commissioning date']);
                      }}
                      error={errors.commissioning?.message}
                      touched={true}
                    />
                  )}
                />
                <CmdLabel id="params-vin" title={f({ id: 'vehicle.vin' })} value={watch('vin')} />
                <Controller
                  name="engineCode"
                  control={control}
                  rules={{
                    required: errors.engineCode ? errors.engineCode.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'form.inputs.vehicles.engineCode' })}
                      id="engineCode"
                      name="engineCode"
                      type="text"
                      onChangeCallback={(value) => {
                        setValue('engineCode', value);
                        trigger('engineCode');
                      }}
                      error={errors.engineCode?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="engineCubature"
                  control={control}
                  rules={{
                    required: errors.engineCubature ? errors.engineCubature.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'form.inputs.vehicles.engineCubature' })}
                      id="engineCubature"
                      name="engineCubature"
                      type="number"
                      currency="l"
                      onChangeCallback={(value) => {
                        setValue('engineCubature', value);
                        trigger('engineCubature');
                      }}
                      error={errors.engineCubature?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="dryWeight"
                  control={control}
                  rules={{
                    required: errors.dryWeight ? errors.dryWeight.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'form.inputs.vehicles.dryWeight' })}
                      id="dryWeight"
                      name="dryWeight"
                      type="number"
                      currency="kg"
                      onChangeCallback={(value) => {
                        setValue('dryWeight', value);
                        trigger('dryWeight');
                      }}
                      error={errors.dryWeight?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="color"
                  control={control}
                  rules={{
                    required: errors.color ? errors.color.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'form.inputs.vehicles.color' })}
                      id="color"
                      name="color"
                      type="color"
                      setValue={(value) => {
                        setValue('color', value);
                        trigger('color');
                      }}
                      error={errors.color?.message}
                      touched={true}
                    />
                  )}
                />
              </CmdBox>

              <CmdBox title={f({ id: 'vehicle.axles' })} id="axles">
                <Controller
                  name="axleCount"
                  control={control}
                  rules={{
                    required: errors.axleCount ? errors.axleCount.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'form.inputs.vehicles.axleCount' })}
                      id="axleCount"
                      name="axleCount"
                      type="number"
                      currency={f({ id: 'form.inputs.vehicles.pieces' })}
                      noDecimal={true}
                      onChangeCallback={(value) => {
                        setValue('axleCount', value);
                        trigger('axleCount');
                      }}
                      error={errors.axleCount?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="axleDriveCount"
                  control={control}
                  rules={{
                    required: errors.axleDriveCount ? errors.axleDriveCount.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'form.inputs.vehicles.axleDriveCount' })}
                      id="axleDriveCount"
                      name="axleDriveCount"
                      type="number"
                      currency={f({ id: 'form.inputs.vehicles.pieces' })}
                      noDecimal={true}
                      onChangeCallback={(value) => {
                        setValue('axleDriveCount', value);
                        trigger('axleDriveCount');
                      }}
                      error={errors.axleDriveCount?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="tireCount"
                  control={control}
                  rules={{
                    required: errors.tireCount ? errors.tireCount.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'form.inputs.vehicles.tireCount' })}
                      id="tireCount"
                      name="tireCount"
                      type="number"
                      currency={f({ id: 'form.inputs.vehicles.pieces' })}
                      noDecimal={true}
                      onChangeCallback={(value) => {
                        setValue('tireCount', value);
                        trigger('tireCount');
                      }}
                      error={errors.tireCount?.message}
                      touched={true}
                    />
                  )}
                />
              </CmdBox>

              <CmdBox title={f({ id: 'form.inputs.vehicles.equipment.section' })} id="equipment">
                <Controller
                  name="equipment"
                  control={control}
                  rules={{
                    required: errors.equipment ? errors.equipment.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdCheckboxGroup
                      {...field}
                      label={f({ id: 'form.inputs.vehicles.equipment' })}
                      id="equipment"
                      name="equipment"
                      options={equipmentOptions}
                      gridTemplateColumns=" 1fr 1fr 1fr "
                      required={false}
                      setValue={(value) => {
                        handleMedicalKitBoolean(value);
                        setValue('equipment', value);
                        trigger('equipment');
                        trackEvent(['vehicle form', 'change', 'Select equipment']);
                      }}
                      error={errors.equipment?.message}
                      touched={true}
                    />
                  )}
                />
                {isMedicalKit &&
                  (medicalKitBoolean ? (
                    <Controller
                      name="first_aid_kit"
                      control={control}
                      rules={{
                        required: errors.first_aid_kit ? errors.first_aid_kit.message : undefined,
                      }}
                      render={({ field }) => (
                        <Calendar
                          {...field}
                          id="first_aid_kit"
                          dateFormat="dd.MM.yyyy"
                          label={f({ id: 'form.inputs.vehicles.firstAidKitValidity' })}
                          onChangeCallback={(value) => {
                            if (value instanceof Date) {
                              setValue('first_aid_kit', value);
                            }
                            trigger('first_aid_kit');
                            trackEvent(['vehicle form', 'change', 'Set First aid kit validity']);
                          }}
                          error={errors.first_aid_kit?.message}
                          touched={true}
                        />
                      )}
                    />
                  ) : (
                    <Controller
                      name="first_aid_kit_dis"
                      control={control}
                      rules={{
                        required: errors.first_aid_kit_dis
                          ? errors.first_aid_kit_dis.message
                          : undefined,
                      }}
                      render={({ field }) => (
                        <CmdInput
                          {...field}
                          id="first_aid_kit_dis"
                          name="first_aid_kit_dis"
                          placeholder={f({
                            id: 'form.inputs.vehicles.firstAidKitValidity.placeholder',
                          })}
                          label={f({ id: 'form.inputs.vehicles.firstAidKitValidity' })}
                          type="text"
                          disabled={true}
                          readonly={true}
                          onChangeCallback={(value) => {
                            setValue('first_aid_kit_dis', value);
                            trigger('first_aid_kit_dis');
                          }}
                          error={errors.first_aid_kit_dis?.message}
                          touched={true}
                        />
                      )}
                    />
                  ))}
              </CmdBox>

              <CmdBox title={f({ id: 'vehicle.inspection' })} id="inspection">
                <Controller
                  name="technicalInspectionValidTo"
                  control={control}
                  rules={{
                    required: errors.technicalInspectionValidTo
                      ? errors.technicalInspectionValidTo.message
                      : undefined,
                  }}
                  render={({ field }) => (
                    <Calendar
                      {...field}
                      label={f({ id: 'form.inputs.vehicles.technicalInspectionValidTo' })}
                      id="technicalInspectionValidTo"
                      dateFormat="dd.MM.yyyy"
                      noTz={true}
                      onChangeCallback={(value) => {
                        setValue('technicalInspectionValidTo', value);
                        trigger('technicalInspectionValidTo');
                        trackEvent(['vehicle form', 'change', 'Set technical inspection validity']);
                      }}
                      error={errors.technicalInspectionValidTo?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="emissionInspectionValidTo"
                  control={control}
                  rules={{
                    required: errors.emissionInspectionValidTo
                      ? errors.emissionInspectionValidTo.message
                      : undefined,
                  }}
                  render={({ field }) => (
                    <Calendar
                      {...field}
                      label={f({ id: 'form.inputs.vehicles.emissionInspectionValidTo' })}
                      id="emissionInspectionValidTo"
                      dateFormat="dd.MM.yyyy"
                      noTz={true}
                      onChangeCallback={(value) => {
                        setValue('emissionInspectionValidTo', value);
                        trigger('emissionInspectionValidTo');
                        trackEvent(['vehicle form', 'change', 'Set emission inspection validity']);
                      }}
                      error={errors.emissionInspectionValidTo?.message}
                      touched={true}
                    />
                  )}
                />
              </CmdBox>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-xxl-6">
            <CmdBox id="basic-information" title={f({ id: 'vehicle.baseData' })}>
              <CmdLabel
                id="customer"
                title={f({ id: 'systemSettings.customer' })}
                value={watch('customer')}
                areLabels={true}
              />
              <CmdLabel
                id="licence"
                title={f({ id: 'vehicle.licenceNumber' })}
                value={watch('licenceNumber')}
                areLabels={true}
              />
              <CmdLabel
                id="manufacturer"
                title={f({ id: 'form.inputs.vehicles.manufacturer' })}
                value={watch('manufacturer')}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-name"
                title={f({ id: 'vehicle.vehicleName' })}
                value={watch('vehicleName')}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-type"
                title={f({ id: 'vehicle.vehicleTypeId' })}
                value={getLabelById(watch('vehicleTypeId'), vehicleTypeOptions)}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-driver"
                title={f({ id: 'vehicle.defaultDriverId' })}
                value={getLabelById(watch('defaultDriverId'), defaultDriverOptions)}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-fuel-card-id"
                title={f({ id: 'vehicle.fuelCardId' })}
                value={String(watch('fuelCardId')) || ''}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-costCenter"
                title={f({ id: 'vehicle.costCenterId' })}
                value={getLabelById(watch('costCenterId'), costCenterOptions)}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-note"
                title={f({ id: 'vehicle.note' })}
                value={watch('note')}
                areLabels={true}
              />
            </CmdBox>

            <CmdBox id="tank" title={f({ id: 'vehicle.vehicleTank' })}>
              <CmdLabel
                id="vehicle-fuel"
                title={f({ id: 'vehicle.fuelTypeId' })}
                value={getLabelById(watch('fuelTypeId'), fuelTypeOptions)}
                areLabels={true}
                tooltip={f({ id: 'vehicle.fuelTypeId.tooltip' })}
              />
              <CmdLabel
                id="vehicle-tank"
                title={f({ id: 'vehicle.totalCapacity' })}
                value={String(watch('totalCapacity'))}
                areLabels={true}
                tooltip={f({ id: 'vehicle.totalCapacity.tooltip' })}
              />
              <CmdLabel
                id="vehicle-consTeoretical"
                title={f({ id: 'vehicle.cons100kmTheoretical' })}
                value={String(watch('cons100kmTheoretical'))}
                areLabels={true}
                tooltip={f({ id: 'vehicle.cons100kmTheoretical.tooltip' })}
              />
              <CmdLabel
                id="vehicle-consNorm"
                title={f({ id: 'vehicle.cons100kmCombined' })}
                value={String(watch('cons100kmNorm'))}
                areLabels={true}
                tooltip={f({ id: 'vehicle.cons100kmCombined.tooltip' })}
              />
            </CmdBox>

            <CmdBox title={f({ id: 'vehicle.vehicleMonitoring' })} id="monitoring">
              <CmdLabel
                id="vehicle-mileAge"
                title={f({ id: 'vehicle.initialMileage' })}
                value={`${watch('initialMileage')} km`}
                areLabels={true}
                tooltip={f({ id: 'vehicle.initialMileage.tooltip' })}
              />
              <CmdLabel
                id="vehicle-item"
                title={f({ id: 'vehicle.unit' })}
                value={watch('unit') || f({ id: 'vehicle.noneF' })}
                areLabels={true}
                tooltip={f({ id: 'vehicle.unit.tooltip' })}
              />
              <CmdLabel
                id="vehicle-simCardPhone"
                title={f({ id: 'table_list.columns.vehicles.phoneNumber' })}
                value={watch('simCardPhoneNumber')}
                areLabels={true}
                tooltip={f({ id: 'vehicle.phoneNumber.tooltip' })}
              />
              <CmdLabel
                id="vehicle-can"
                title={f({ id: 'vehicle.can' })}
                value={watch('can') || f({ id: 'vehicle.noneM' })}
                areLabels={true}
                tooltip={f({ id: 'vehicle.can.tooltip' })}
              />
              <CmdLabel
                id="cardReaderType"
                title={f({ id: 'vehicleSettings.cardReaderType' })}
                value={getCardReaderType()}
                areLabels={true}
                tooltip={f({ id: 'vehicleSettings.cardReaderType.tooltip' })}
              />
              <CmdLabel
                id="vehicleBlockingAvailable"
                title={f({ id: 'vehicleSettings.vehicleBlockingAvailable' })}
                value={getVehicleBlockingAvailableText()}
                areLabels={true}
                tooltip={f({ id: 'vehicleSettings.vehicleBlockingAvailable.tooltip' })}
              />
              <CmdLabel
                id="monitoring-private-default"
                title={f({ id: 'vehicle.privateVehicleDefault' })}
                value={watch('privateVehicleDefault')}
              />
              <CmdLabel
                id="vehicle-privateVehicle"
                title={f({ id: 'vehicle.privateVehicle' })}
                value={watch('privateVehicle')}
                areLabels={true}
                tooltip={f({ id: 'vehicle.privateVehicle.tooltip' })}
              />
              <CmdLabel
                id="vehicle-auto-correction"
                title={f({ id: 'form.inputs.vehicles.autoCorrection' })}
                value={getAutoCorrectionValue()}
                areLabels={true}
                tooltip={f({ id: 'vehicle.autoCorrection.tooltip' })}
              />
              <CmdLabel
                id="vehicle-ecoDrive"
                title={f({ id: 'form.inputs.vehicles.edEnabled' })}
                value={
                  watch('edEnabled')
                    ? f({ id: 'enum.status.enabledM' })
                    : f({ id: 'enum.status.disabledM' })
                }
                areLabels={true}
                tooltip={f({ id: 'vehicle.edEnabled.tooltip' })}
              />
              <CmdLabel
                id="bluecoinEnabled"
                title={f({ id: 'vehicleSettings.bluecoinEnabled' })}
                value={
                  watch('bluecoinEnabled')
                    ? f({ id: 'vehicleSettings.enabledM' })
                    : f({ id: 'vehicleSettings.disabledM' })
                }
                areLabels={true}
                tooltip={f({ id: 'vehicleSettings.bluecoinEnabled.tooltip' })}
              />
            </CmdBox>
          </div>
          <div className="col-xxl-6">
            <CmdBox id="params" title={f({ id: 'vehicle.technicalParams' })}>
              <CmdLabel
                id="vehicle-model"
                title={f({ id: 'vehicle.model' })}
                value={watch('model')}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-year"
                title={f({ id: 'vehicle.manufactureYear' })}
                value={watch('manufactureYear')}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-commissioning"
                title={f({ id: 'vehicle.commissioning' })}
                value={
                  watch('commissioning') ? dayjs(watch('commissioning')).format('DD.MM.YYYY') : ''
                }
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-vin"
                title={f({ id: 'vehicle.vin' })}
                value={watch('vin')}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-engine-code"
                title={f({ id: 'form.inputs.vehicles.engineCode' })}
                value={watch('engineCode')}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-engineCubature"
                title={f({ id: 'form.inputs.vehicles.engineCubature' })}
                value={watch('engineCubature')}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-weight"
                title={f({ id: 'form.inputs.vehicles.dryWeight' })}
                value={watch('dryWeight')}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-color"
                title={f({ id: 'form.inputs.vehicles.color' })}
                value={watch('color')}
                areLabels={true}
              />
            </CmdBox>

            <CmdBox id="axles" title={f({ id: 'vehicle.axles' })}>
              <CmdLabel
                id="vehicle-axleCount"
                title={f({ id: 'form.inputs.vehicles.axleCount' })}
                value={String(watch('axleCount'))}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-axleDriveCount"
                title={f({ id: 'form.inputs.vehicles.axleDriveCount' })}
                value={String(watch('axleDriveCount'))}
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-tireCount"
                title={f({ id: 'form.inputs.vehicles.tireCount' })}
                value={String(watch('tireCount'))}
                areLabels={true}
              />
            </CmdBox>

            <CmdBox title={f({ id: 'form.inputs.vehicles.equipment.section' })} id="equipment">
              <CmdLabel
                id="vehicle-equipment"
                title={f({ id: 'form.inputs.vehicles.equipment' })}
                value={getValueByOptions(equipmentOptions, watch('equipment'))}
                areLabels={true}
              />
              {watch('first_aid_kit') && (
                <CmdLabel
                  id="vehicle-firsAid"
                  title={f({ id: 'form.inputs.vehicles.firstAidKitValidity' })}
                  value={
                    medicalKitBoolean ? dayjs(watch('first_aid_kit')).format('DD.MM.YYYY') : ''
                  }
                  areLabels={true}
                />
              )}
            </CmdBox>

            <CmdBox id="inspection" title={f({ id: 'vehicle.inspection' })}>
              <CmdLabel
                id="vehicle-technicalInspection"
                title={f({ id: 'form.inputs.vehicles.technicalInspectionValidTo' })}
                value={
                  watch('technicalInspectionValidTo')
                    ? dayjs(watch('technicalInspectionValidTo')).format('DD.MM.YYYY')
                    : ''
                }
                areLabels={true}
              />
              <CmdLabel
                id="vehicle-emissionInspection"
                title={f({ id: 'form.inputs.vehicles.emissionInspectionValidTo' })}
                value={
                  watch('emissionInspectionValidTo')
                    ? dayjs(watch('emissionInspectionValidTo')).format('DD.MM.YYYY')
                    : ''
                }
                areLabels={true}
              />
            </CmdBox>
          </div>
        </div>
      )}
      <div>
        {isModalOpened &&
          PermissionsService.hasAccess(
            PERMISSION_REFUELING_CARDS,
            ['create'],
            props.customerId
          ) && (
            <FuelCard
              handleClose={closeModal}
              customerName={watch('customer')}
              handleNewFuelCard={handleNewFuelCard}
              customerId={props.customerId || 0}
              vehicleId={props.id || 0}
            />
          )}
      </div>
    </CmdSubpage>
  );
}
